<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core'
import { faTrash, faTriangleExclamation, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import MidiLibraryApiClient from '../js/MidiLibraryApiClient.js';

library.add(faTrash, faTriangleExclamation, faChevronDown, faChevronRight);

const SEARCH_DEBOUNCE_MS = 300;

// Edits the name and cc label map for a single device slot
// (settings.devices[i]). Mutates `device` in place, same pattern as the
// effect form components — no explicit save, closing just stops editing.
//
// Also offers loading a preset from the MIDI device library: searching and
// picking a device copies in its known CC names and records its
// openmidi_slug on the device slot. Existing CC labels are never
// overwritten — a CC that's already labelled differently from the library
// is left alone but flagged, with the library's value shown as a hint.
export default {
  name: 'DeviceEditModal',

  components: {
    FontAwesomeIcon,
  },

  props: {
    show: {
      type: Boolean,
      required: true,
    },

    device: {
      type: Object,
      default: null,
    },

    channel: {
      type: Number,
      default: null,
    },

    // True when this channel is Midulate's own. Two things follow from it,
    // and they are the same fact rather than two settings:
    //
    //   - the device name is fixed to "Midulate" and not user-editable;
    //   - the MIDI library section is not offered at all, because Midulate is
    //     not a device in the library — it is the one doing the looking, so a
    //     search could only ever return someone else's pedal and label
    //     Midulate's own CCs with its names (MDL-390).
    //
    // Deliberately not folded into showLibrarySearch: that flag is about
    // connectivity and renders "Connect Midulate to a WiFi network …",
    // which would be a misleading reason here.
    isOwnChannel: {
      type: Boolean,
      default: false,
    },

    // Injectable so tests don't need to stub global fetch; defaults to a
    // real client hitting /api/midi_devices.
    apiClient: {
      type: Object,
      default: () => new MidiLibraryApiClient(),
    },

    // Whether to offer "load a preset from the MIDI library" — requires a
    // real path to the internet, which isn't guaranteed on-device (see
    // DeviceSettingsForm's showLibrarySearch doc).
    showLibrarySearch: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['close'],

  data() {
    return {
      newCcNumber: '',
      libraryExpanded: false,
      librarySearchQuery: '',
      librarySearchResults: [],
      // Index into librarySearchResults for keyboard navigation; -1 = none
      // highlighted. Reset whenever the result set changes so it never
      // points at a stale item.
      highlightedResultIndex: -1,
      searchDebounceTimer: null,
      presetLoading: false,
      presetLoadedDevice: null, // { manufacturer, name } of the last preset applied this session
      presetConflicts: {}, // { [ccNumber]: libraryLabel } for CCs that differ and weren't overwritten
    };
  },

  computed: {
    ccEntries() {
      const cc = this.device?.cc;
      if (!cc) return [];
      return Object.keys(cc)
        .map(Number)
        .sort((a, b) => a - b)
        .map((number) => ({ number, label: cc[number] }));
    },
  },

  watch: {
    show(val) {
      if (val) {
        this.newCcNumber = '';
        this.libraryExpanded = false;
        this.librarySearchQuery = '';
        this.librarySearchResults = [];
        this.highlightedResultIndex = -1;
        this.presetLoadedDevice = null;
        this.presetConflicts = {};
        this.$nextTick(() => this.$refs.dialog.showModal());
      } else {
        this.$refs.dialog.close();
      }
    },
  },

  methods: {
    addCc() {
      const number = Number(this.newCcNumber);
      if (!Number.isInteger(number) || number < 0 || number > 127) return;

      if (!this.device.cc) this.device.cc = {};
      if (!(number in this.device.cc)) this.device.cc[number] = '';

      this.newCcNumber = '';
    },

    removeCc(number) {
      if (!this.device?.cc) return;
      delete this.device.cc[number];
    },

    close() {
      this.$emit('close');
    },

    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      const query = this.librarySearchQuery;
      this.highlightedResultIndex = -1;

      if (!query.trim()) {
        this.librarySearchResults = [];
        return;
      }

      this.searchDebounceTimer = setTimeout(async () => {
        try {
          this.librarySearchResults = await this.apiClient.search(query);
        } catch {
          this.librarySearchResults = [];
        }
        this.highlightedResultIndex = -1;
      }, SEARCH_DEBOUNCE_MS);
    },

    // Arrow keys move the highlight, Enter selects it (or the sole result if
    // none is highlighted yet), Escape dismisses the list without clearing
    // the query — matching standard combobox/autocomplete behaviour.
    onSearchKeydown(e) {
      const count = this.librarySearchResults.length;
      if (count === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.highlightedResultIndex = Math.min(this.highlightedResultIndex + 1, count - 1);
        this.scrollHighlightedResultIntoView();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.highlightedResultIndex = Math.max(this.highlightedResultIndex - 1, 0);
        this.scrollHighlightedResultIntoView();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const index = this.highlightedResultIndex >= 0 ? this.highlightedResultIndex : 0;
        this.selectLibraryDevice(this.librarySearchResults[index]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.librarySearchResults = [];
        this.highlightedResultIndex = -1;
      }
    },

    scrollHighlightedResultIntoView() {
      this.$nextTick(() => {
        // jsdom (tests) doesn't implement scrollIntoView at all.
        this.$refs.resultItems?.[this.highlightedResultIndex]?.scrollIntoView?.({ block: 'nearest' });
      });
    },

    async selectLibraryDevice(result) {
      this.presetLoading = true;
      try {
        const detail = await this.apiClient.getDevice(result.id);
        this.applyPreset(detail);
        this.presetLoadedDevice = { manufacturer: detail.manufacturer, name: detail.name };
      } finally {
        this.presetLoading = false;
        this.librarySearchQuery = '';
        this.librarySearchResults = [];
        this.highlightedResultIndex = -1;
      }
    },

    applyPreset(libraryDevice) {
      this.device.openmidi_slug = libraryDevice.openmidi_slug;
      if (!this.device.name) this.device.name = libraryDevice.name;
      if (!this.device.cc) this.device.cc = {};

      const conflicts = {};
      for (const mapping of libraryDevice.cc_mappings) {
        const existing = this.device.cc[mapping.cc_number];
        if (existing === undefined) {
          this.device.cc[mapping.cc_number] = mapping.label;
        } else if (existing !== mapping.label) {
          conflicts[mapping.cc_number] = mapping.label;
        }
      }
      this.presetConflicts = conflicts;
    },
  },
}
</script>
<template>
  <dialog ref="dialog" class="modal" @close="close">
    <!-- The dialog is always in the DOM (so showModal()/close() can be
         called via $refs), but `device` is null until a channel row is
         clicked — guard the body so it doesn't render against null. -->
    <div v-if="device" class="modal-box max-w-lg">
      <h3 class="text-lg font-bold mb-4">Channel {{ channel }}</h3>

      <fieldset class="fieldset mb-2">
        <legend class="fieldset-legend">Device name</legend>
        <input type="text" class="input !w-full" v-model="device.name" :disabled="isOwnChannel" />
      </fieldset>

      <div class="divider my-2"></div>

      <div v-if="device.openmidi_slug" class="text-sm text-base-content/60 mb-2">
        Linked to <span class="font-mono">{{ device.openmidi_slug }}</span>
      </div>

      <!-- Not offered on Midulate's own channel: there is nothing in the
           library to find, so the section is hidden rather than shown with an
           explanation — the name field beside it is already locked, so the
           modal reads as "this one is the device itself" (MDL-390).

           The template carries the trailing divider as well as the fieldset:
           the section sits between two dividers, so hiding the fieldset alone
           left them touching. -->
      <template v-if="!isOwnChannel">
        <fieldset class="fieldset mb-2">
          <legend
            class="fieldset-legend cursor-pointer flex items-center gap-1 select-none"
            @click="libraryExpanded = !libraryExpanded"
          >
            <FontAwesomeIcon :icon="libraryExpanded ? 'chevron-down' : 'chevron-right'" class="text-xs" />
            Load from MIDI Library
          </legend>

          <template v-if="libraryExpanded">
            <template v-if="showLibrarySearch">
              <p class="mb-2">
                Search for this device to copy in its known CC names. CC names you've already set won't be
                overwritten &mdash; any that differ from the library are flagged below instead.
              </p>

              <!-- relative wrapper + absolute results list: the list used to sit
                   in normal flow, so results appearing/clearing as you type
                   resized the modal around it. Positioning it absolutely makes
                   it float over the content below instead, like a normal
                   autocomplete. -->
              <div class="relative">
                <input
                  type="text"
                  class="input input-sm !w-full"
                  placeholder="Search devices..."
                  v-model="librarySearchQuery"
                  @input="onSearchInput"
                  @keydown="onSearchKeydown"
                />

                <ul
                  v-if="librarySearchResults.length > 0"
                  class="menu bg-base-100 border border-base-300 rounded-box shadow-lg absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto flex-nowrap"
                >
                  <li
                    v-for="(result, idx) in librarySearchResults"
                    :key="result.id"
                    ref="resultItems"
                  >
                    <a
                      :class="{ 'menu-active': idx === highlightedResultIndex }"
                      @click="selectLibraryDevice(result)"
                      @mouseenter="highlightedResultIndex = idx"
                    >{{ result.manufacturer }} {{ result.name }}</a>
                  </li>
                </ul>
              </div>

              <div v-if="presetLoadedDevice" class="text-sm text-success mt-2">
                Loaded {{ presetLoadedDevice.manufacturer }} {{ presetLoadedDevice.name }}.
                <span v-if="Object.keys(presetConflicts).length > 0">
                  {{ Object.keys(presetConflicts).length }} CC name{{ Object.keys(presetConflicts).length === 1 ? '' : 's' }}
                  already set differently &mdash; see below.
                </span>
              </div>
            </template>
            <p v-else class="text-sm text-base-content/50">
              Connect Midulate to a WiFi network in order to look up devices in the MIDI library.
            </p>
          </template>
        </fieldset>

        <div class="divider my-2"></div>
      </template>

      <fieldset class="fieldset">
        <legend class="fieldset-legend">CC names</legend>
        <p class="mb-2">
          Label the CC numbers this device uses. Unlabelled CCs still work &mdash; they'll just show as a plain
          number.
        </p>

        <!-- DaisyUI's .input component sets width: clamp(3rem, 20rem, 100%)
             — a 320px preferred width. That rule lives in a CSS cascade
             layer appended after Tailwind's `utilities` layer (DaisyUI's
             "daisyui" layer isn't part of this project's explicit
             `@layer theme, base, components, utilities;` order, so per the
             cascade-layers spec it's implicitly appended at the end),
             meaning it beats width/flex utility classes regardless of
             their specificity. That's what was actually forcing each CC
             row wider than the modal on mobile — flex-1/min-w-0 alone
             can't win that fight. `!` forces these specific overrides to
             `!important`, which does win regardless of layer order. -->
        <div class="max-h-72 overflow-y-auto">
          <div v-if="ccEntries.length === 0" class="text-sm text-base-content/40 py-2">
            No CC names added yet.
          </div>
          <div
            v-for="entry in ccEntries"
            :key="entry.number"
            class="grid grid-cols-[20px_1fr_20px] items-center flex-wrap gap-2 mb-2 p-1 rounded"
            :class="{ 'bg-warning/10': presetConflicts[entry.number] }"
          >
            <span class="font-mono text-sm w-10 shrink-0">{{ entry.number }}</span>
            <div class="min-w-0">
              <input
                type="text"
                class="input input-sm flex-1 min-w-0 !w-full"
                placeholder="Label"
                v-model="device.cc[entry.number]"
              />
              <div v-if="presetConflicts[entry.number]" class="text-xs text-warning flex items-center gap-1 mt-1">
                <FontAwesomeIcon icon="triangle-exclamation" />
                <span>Library suggests &ldquo;{{ presetConflicts[entry.number] }}&rdquo;</span>
              </div>
            </div>
            <button
              type="button"
              class="btn btn-ghost btn-xs shrink-0"
              :aria-label="`Remove CC ${entry.number} label`"
              @click="removeCc(entry.number)"
            >
              <FontAwesomeIcon icon="trash" />
            </button>
          </div>
        </div>

        <div class="flex items-center flex-wrap gap-2 mt-2 pt-2">
          <input
            type="number"
            min="0"
            max="127"
            class="input input-sm !w-16 shrink-0"
            placeholder="CC #"
            v-model="newCcNumber"
            @keyup.enter="addCc"
          />
          <button type="button" class="btn btn-sm shrink-0" @click="addCc">+ Add CC</button>
        </div>
      </fieldset>

      <div class="modal-action">
        <form method="dialog">
          <button class="btn btn-primary btn-sm" @click="close">Done</button>
        </form>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="close">close</button>
    </form>
  </dialog>
</template>
